import { useQuery } from "@tanstack/react-query";
import {useFormContext, useWatch} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormAutocomplete } from "../../../../common/form-fields-wrappers";
import { LOCALE_DATE_FORMAT } from "../../../../config";
import { ICustomerDataForm } from "../../../../models/models";
import { getIssuePlaces } from "../../../services/http.services";
import countryMappings from "./country-mappings";
import { useCallback, useEffect } from "react";

const IdIssuingPlaceField = ({ referenceProp, disabled }: { referenceProp: string, disabled: boolean }) => {
	const { t, i18n } = useTranslation('network');

	const form = useFormContext();

	const [
		idIssuingCountry,
		idIssuingProvince,
		idIssuingPlace,
		idIssueDate,
		idDocFront,
		idDocBack
	] = useWatch({
		name: [
			`${referenceProp}.idIssuingCountry`,
			`${referenceProp}.idIssuingProvince`,
			`${referenceProp}.idIssuingPlace`,
			`${referenceProp}.idIssueDate`,
			`${referenceProp}.idDocFront`,
			`${referenceProp}.idDocBack`,
		]
	})

	const issuePlacesQuery = useQuery({
		queryKey: ['ntw_issue_places', {
			provinceCode: idIssuingProvince?.codeProvince,
			issueDate: idIssueDate ?? idIssueDate?.format(LOCALE_DATE_FORMAT)
		}],
		queryFn: () => {
			if (
				idIssueDate &&
				!idIssueDate.isValid()
			) {
				return null;
			}
			return getIssuePlaces({
				provinceCode: (idIssuingCountry === 'IT')
					? idIssuingProvince?.codeProvince
					: null,
				issueDate: idIssueDate?.format(LOCALE_DATE_FORMAT)
			})
		}
	});

	const options = [
		...(issuePlacesQuery?.data ?? []),
		...(idIssuingCountry && !["IT", "FR"].includes(idIssuingCountry) ? [{
			description: idIssuingCountry,
			label: idIssuingCountry,
			code: idIssuingCountry
		}] : []),
	]

	useEffect(() => {
		if (idIssuingCountry && idIssuingCountry === 'IT') {
			return;
		} else if (idIssuingCountry && idIssuingCountry === 'FR') {
			const newValue = issuePlacesQuery.data?.find(p => countryMappings[p.description] === idIssuingCountry);
			form.setValue(`${referenceProp}.idIssuingPlace`, newValue);
		} else if (idIssuingCountry) {
			form.setValue(`${referenceProp}.idIssuingPlace`, {label: idIssuingCountry, description: idIssuingCountry, code: idIssuingCountry});
		}
	}, [idIssuingCountry, issuePlacesQuery.data]);

	useEffect(() => {
		if (idIssuingCountry === 'IT' && !idIssuingProvince) {
			form.setValue(`${referenceProp}.idIssuingPlace`, null);
		}
	}, [idIssuingCountry, idIssuingProvince]);

	const getCountryLabel = useCallback((opt: any) => {
		if (idIssuingCountry === 'IT') {
			return opt.description;
		}

		if (idIssuingCountry === 'FR') {
			return t(`countries.${opt.code}`);
		}

		return opt.description;
	}, [idIssuingCountry, i18n.language]);

	return (
		<FormAutocomplete<ICustomerDataForm>
			name={`${referenceProp}.idIssuingPlace`}
			label={t("customerData.form.label.idIssuingPlace")}
			options={options}
			required={idDocFront && idDocBack}
			loading={issuePlacesQuery.isInitialLoading}
			disabled={disabled}
			getOptionLabel={getCountryLabel}
		/>
	)
}

export default IdIssuingPlaceField
